(ns viesti.frontend.core
  (:require [clojure.string :as str]
            [cognitect.transit :as transit]
            [promesa.core :as p]
            [re-frame.core :as rf]
            [re-frame.db]
            [viesti.core :as v]))

;;
;; Transit
;;
;; https://clojureverse.org/t/how-to-write-instants-for-transit-responses/5847

(defn transit-decoder
  ([] (transit-decoder
       (transit/reader :json {:handlers {"Instant" (transit/read-handler #(js/Date. %))}})))
  ([reader] (fn [x] (when-not (str/blank? x)
                      ;; xxx(Allan): I tried to add this to the kondo config, didn't work.
                      ;;             It's required because kondo thinks this is the clj version
                      ;;             of `read`, which is single arity
                      #_{:clj-kondo/ignore [:invalid-arity]} (transit/read reader x)))))

(defn transit-encoder
  ([] (transit-encoder
       (transit/writer :json {:handlers {js/Date (transit/write-handler (constantly "Instant") #(.toISOString %))}})))
  ([writer] (fn [x] (transit/write writer x))))

;;
;; Chaining
;;

(declare dispatch)

(defn handle [x]
  (cond (map? x) #(dispatch x)
        (vector? x) #(rf/dispatch (conj x %))
        :else x))

;;
;; Defaults
;;

(def -default-request* (atom nil))

(def -default-request
  (delay
   {:uri "/api/dispatch"
    :method :post
    :headers {"Content-Type" "application/transit+json"
              "Accept" "application/transit+json"}
    :encode (transit-encoder)
    :decode (transit-decoder)
    :on-success [[:cqrs/set-result]]
    :on-failure [[:cqrs/set-error] [:cqrs/log]]}))

(defn -initialized! [] (when-not @-default-request* (throw (ex-info "http not initialized" {}))))

(defn -merge-and-concat [event-keyseq & maps]
  (letfn [(ensure-wrapped [m]
            (->> (map (fn [[k v]]
                        [k (if-not (vector? (first v)) [v] v)]) m)
                 (into {})))]
    (->> (map #(-> (select-keys % event-keyseq)
                   (ensure-wrapped)) maps)
         (apply merge-with into)
         (conj (vec maps))
         (apply merge))))

(defn -merge-requests [& requests]
  (apply -merge-and-concat [:on-success :on-failure] requests))

(defn init!
  ([] (reset! -default-request* @-default-request))
  ([options] (reset! -default-request* (-merge-requests @-default-request options))))

;;
;; fetch
;;

(defn -query-string [params]
  (let [-k (fn [k]
             (cond
               (qualified-keyword? k) (str (namespace k) "/" (name k))
               (keyword? k) (name k)
               :else k))]
    (->> params
         (map (fn [[k v]]
                (str (-k k)
                     "="
                     (js/encodeURIComponent v))))
         (interpose "&")
         (apply str "?"))))

(defn -headers [response]
  (->> (.-headers response)
       (.entries)
       (es6-iterator-seq)
       (map vec)
       (into {})))

(defn -success? [status] (<= 200 status 299))

(defn -now [] (.getTime (js/Date.)))

(defn fetch [{:keys [uri method headers query-params encode decode then catch] :as options :or {method :get}}]
  (let [uri (cond-> uri (seq query-params) (str (-query-string query-params)))
        options (clj->js (-> options
                             (assoc :method (-> method name str/upper-case))
                             (cond-> headers (update :headers clj->js))
                             (dissoc :uri :encode :decode)
                             (cond-> encode (update :body encode))))]
    (-> (p/let [response (js/fetch uri options)]
          (p/let [body (.text response)]
            (let [status (.-status response)]
              ;; CQRS handler is checking for success status, this must
              ;; resolve even for 400<=status<=599, same as JS fetch promise.
              {:status status
               :headers (-headers response)
               :body (cond-> body decode decode)})))
        (cond-> then (p/then (handle then)))
        (cond-> catch (p/catch (handle catch))))))

;;
;; Dispatcher
;;

(defn -on [events request response]
  (doseq [event (keep identity events)]
    (assert (vector? event))
    (rf/dispatch (conj event request response))))

(defn -log [{:keys [start type]} {:keys [status]}]
  (js/console.log (if (-success? status) "\u2705" "\u274C") (str (- (-now) start) "ms") {:type type}))

(defn -error! [type] (js/console.log "invalid action type" type))

(defn dispatch [request]
  (assert (not (vector? request)) "cqrs request should be a map with :type or a Keyword")
  (let [{:keys [on-success on-failure type data authorize then catch]
         :as request} (-> (-merge-requests @-default-request* (v/-event request))
                          (assoc :start (-now)))
        fetch-request (-> request
                          (dissoc :type :data :path :authorize :on-success :on-failure :then :catch)
                          (assoc :body {:type type, :data (if (fn? data) (data) data)})
                          (cond-> authorize (authorize)))]
    (rf/dispatch [:cqrs/set-pending request])
    (-> (fetch fetch-request)
        (p/then (fn [{:keys [status body] :as response}]
                  (when (not= js/window.__LEMONPI_ENV__ "production")
                    (-log request response))
                  (if (-success? status)
                    (do (when on-success (-on on-success request response))
                        (p/resolved body))
                    (do (when on-failure (-on on-failure request response))
                        (p/rejected body)))))
        (cond-> then (p/then (handle then)))
        (cond-> catch (p/catch (handle catch))))))

;;
;; re-frame
;;

(defn -error [available [_ type]]
  (if-not available
    {:type ::v/error}
    (if-let [[_ v] (find available type)] v (-error! type))))

(rf/reg-event-fx :cqrs/send
  (fn [_ [_ request]]
    {:cqrs/send request}))

(rf/reg-fx :cqrs/send
  (fn [request] (dispatch (v/-event request))))

(rf/reg-event-fx :cqrs/log
  (fn [_ [_ {:keys [type]} {:keys [status body]}]]
    (js/console.log {:status status, :type type, :body body})))

(defn set-result [db req f]
  (let [path (or (:path req)
                 (conj [:cqrs] (:type req)))]
    (assoc-in db path f)))

(defn update-result [db req f]
  (let [path (or (:path req)
                 (conj [:cqrs] (:type req)))]
    (update-in db path f)))

(rf/reg-event-db :cqrs/set-result
  (fn [db [_ req {:keys [body] :as _response}]]
    (set-result db req {:pending false
                        :initial-pending false
                        :value body})))

(rf/reg-event-db :cqrs/set-error
  (fn [db [_ req {:keys [body] :as _response}]]
    (set-result db req
                {:pending false
                 :initial-pending false
                 :error body})))

(rf/reg-event-db :cqrs/set-pending
  (fn [db [_ req]]
    ;; Store pending and initial-pending (first load) status separately,
    ;; as it is usual use-case to need to check if either is true.
    ;; One state enum makes would make it harder to check if either pending is true.
    ;;
    ;; Only mark :pending-initial when running this query the first time.
    ;; This also doesn't remove :value or :error from previous uses,
    ;; so the components can choose to keep showing the old data when running
    ;; the query again.
    (update-result db req
                   (fn [v]
                     (assoc v
                            :pending true
                            :initial-pending (nil? (:initial-pending v)))))))

(rf/reg-sub :cqrs/db
  (fn [db] (:cqrs db)))

(rf/reg-sub :cqrs/get
  :<- [:cqrs/db]
  (fn [db [_ type]] (get db type)))

(rf/reg-sub :cqrs/value
  :<- [:cqrs/db]
  (fn [db [_ type]] (get-in db [type :value])))

(rf/reg-sub :cqrs/action
  :<- [:cqrs/get :system/actions]
  (fn [actions [_ type]]
    (when actions
      (or (get actions type)
          (-error! type)))))

(rf/reg-sub :cqrs/input
  (fn [[_ type]] (rf/subscribe [:cqrs/action type]))
  (fn [action] (:input action)))

(rf/reg-sub :cqrs/output
  (fn [[_ type]] (rf/subscribe [:cqrs/action type]))
  (fn [action] (:output action)))

(rf/reg-sub :cqrs/error
  :<- [:cqrs/get :system/available-actions]
  -error)

(rf/reg-sub :cqrs/available
  :<- [:cqrs/get :system/available-actions]
  (complement -error))

;;
;; fsm
;;

#_(rf/reg-sub :fsm/flows :<- [:cqrs/db] (fn [db] (->> db :fsm/flows (map (fn [[k v]] [k (vf/-create v)])) (into {}))))
#_(rf/reg-sub :fsm/flow :<- [:fsm/flows] (fn [flows [_ type]] (get flows type)))

#_(rf/reg-sub :fsm/fsm (fn [data] (:fsm data)))
#_(rf/reg-sub :fsm/next :<- [:fsm/fsm] (fn [data [_ id]] (get-in data [id])))

#_(rf/reg-sub :fsm/error :<- [:cqrs/get :fsm/available] -error)
#_(rf/reg-sub :fsm/available :<- [:cqrs/get :fsm/available] (complement -error))
